import React, { useEffect, useState, useRef } from "react";
import { connect } from "react-redux";
import { useHistory } from "react-router-dom";
import useEmpresa from "../../../hooks/useEmpresa";
import IngresoActions from "../../../stores/ingreso/Actions";
import CustomLayout from "../../../components/CustomLayout/CustomLayout";
import CustomButton from "../../../components/CustomButton/CustomButton";
import CustomSeparator from "../../../components/Common/CustomSeparator";
import { Section } from "../../../components/CustomLayout/CustomLayoutStyles";
import TittleLine from "../../../components/Common/TittleLine";
import CustomInput from "../../../components/CustomForm/CustomInput";
import CustomSelect from "../../../components/CustomForm/CustomSelect";
import {
  FormWrapper,
  Form,
  FormGroup,
  Label,
  TextArea,
} from "../../../components/CustomForm/FormStyles";
import CheckRender from "../../../components/Common/CheckRender";
import CustomAlert from "../../../components/Common/CustomAlert";
import ModalLoader from "../../../components/Common/ModalLoader";
import Colors from "../../../themes/Colors";
import IngresoService from "../../../services/ingreso/IngresoService";
import { dataMotivoEgreso } from "../../../utils/LocalData";
import {
  TIPO_PSS_NO_MEDICO_JIRA,
  PLAN_PDSS_JIRA,
  MAXIMO_MEDICOS_ADICIONALES,
} from "../utils/IngresoConstant";
import PrestadorActions from "../../../stores/prestador/Actions";
import { isEmptyValue } from "../../../utils/StringUtil";
import { isValidNumber, isValidEmail } from "../../../utils/ValidationUtil";
import { EMPRESA } from "../../../utils/Constant";
import DocumentUploadRow from "../components/DocumentUploadRow";
import { validateUploadFile, buildAlertConfig } from "../utils/HojaQuirurgicaUtils";


const HojaQuirurgicaScreen = (props) => {
  const { formHojaQuirurgicaData, authUserData } = props;
  const history = useHistory();
  const empresa = useEmpresa();
  const [medicoAdicionalInput, setMedicoAdicionalInput] = useState("");
  const [uploadingStatus, setUploadingStatus] = useState({});
  const [localFiles, setLocalFiles] = useState({});
  const [hasInitialized, setHasInitialized] = useState(false);
  const isPrimera = empresa?.CODIGO === EMPRESA.PRIMERA.CODIGO;
  const createdUrlsRef = useRef([]);
  
  useEffect(() => {
    if (!authUserData || !empresa) {
      return;
    }

    if (!isPrimera || !authUserData?.prestador?.permiteHojaQuirurgica) {
      history.push("/dashboard");
      return;
    }

    if (!hasInitialized) {
      setHasInitialized(true);
      props.obtenerCamposHojaQuirurgica();

      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        tipoPss: TIPO_PSS_NO_MEDICO_JIRA,
        plan: PLAN_PDSS_JIRA,
        documentos: {
          hojaQuirurgica: null,
          hojaAnestesia: null,
          estudiosRealizados: null,
          historiaClinica: null,
          otros: null,
        },
      });
    }
  }, [authUserData, empresa, isPrimera, hasInitialized, history, props, formHojaQuirurgicaData]);

  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      createdUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleAddMedico = () => {
    if (medicoAdicionalInput.trim() !== "") {
      const currentMedicos = formHojaQuirurgicaData.medicosAdicionales || [];
      if (currentMedicos.length >= MAXIMO_MEDICOS_ADICIONALES) {
        props.formHojaQuirurgica({
          ...formHojaQuirurgicaData,
          alertConfig: buildAlertConfig(
            "error",
            "Límite superado",
            `Solo se permite agregar hasta ${MAXIMO_MEDICOS_ADICIONALES} médicos adicionales.`,
            () => {
              props.formHojaQuirurgica({
                ...formHojaQuirurgicaData,
                showAlert: false,
              });
            }
          ),
          showAlert: true,
        });
        return;
      }
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        medicosAdicionales: [...currentMedicos, medicoAdicionalInput.trim()],
      });
      setMedicoAdicionalInput("");
    }
  };

  const handleRemoveMedico = (indexToRemove) => {
    const currentMedicos = formHojaQuirurgicaData.medicosAdicionales || [];
    props.formHojaQuirurgica({
      ...formHojaQuirurgicaData,
      medicosAdicionales: currentMedicos.filter(
        (_, index) => index !== indexToRemove,
      ),
    });
  };

  const handleMedicoAdicionalInputChange = (val, applyFilter = true) => {
    setMedicoAdicionalInput(val);
    if (!applyFilter) return;

    if (isEmptyValue(val)) {
      return;
    }
    if (isValidNumber(val)) {
      props.getMedicos({ codigoMedico: val });
    } else if (val.length >= 3) {
      props.getMedicos({ nombreMedico: val });
    }
  };

  const handleMedicoTratantePrincipalChange = (val, applyFilter = true) => {
    props.formHojaQuirurgica({
      ...formHojaQuirurgicaData,
      medicoTratantePrincipal: val,
    });
    if (!applyFilter) return;

    if (isEmptyValue(val)) {
      return;
    }
    if (isValidNumber(val)) {
      props.getMedicos({ codigoMedico: val });
    } else if (val.length >= 3) {
      props.getMedicos({ nombreMedico: val });
    }
  };

  const handleFileUpload = (key) => (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset target value so the user can upload the same file again if they deleted it
    e.target.value = "";

    const validation = validateUploadFile(file);
    if (!validation.isValid) {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        alertConfig: buildAlertConfig(
          "error",
          validation.title,
          validation.message,
          () => {
            props.formHojaQuirurgica({
              ...formHojaQuirurgicaData,
              showAlert: false,
            });
          }
        ),
        showAlert: true,
      });
      return;
    }

    const tempId = `local_${key}_${Date.now()}`;
    props.formHojaQuirurgica({
      ...formHojaQuirurgicaData,
      documentos: {
        ...formHojaQuirurgicaData.documentos,
        [key]: {
          fileName: file.name,
          file: file,
          tempId: tempId,
        },
      },
    });
    setLocalFiles((prev) => ({ ...prev, [tempId]: file }));
  };

  const handleRemoveFile = (key, tempId) => () => {
    props.formHojaQuirurgica({
      ...formHojaQuirurgicaData,
      documentos: {
        ...formHojaQuirurgicaData.documentos,
        [key]: null,
      },
    });

    if (tempId) {
      setLocalFiles((prev) => {
        const copy = { ...prev };
        delete copy[tempId];
        return copy;
      });
    }
  };

  const handleViewFile = (docInfo) => () => {
    const file = docInfo?.file || (docInfo?.tempId ? localFiles[docInfo.tempId] : null);
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      createdUrlsRef.current.push(fileUrl);
      window.open(fileUrl, "_blank");
    } else {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        alertConfig: buildAlertConfig(
          "info",
          "Información",
          "El archivo no se puede visualizar localmente.",
          () => {
            props.formHojaQuirurgica({
              ...formHojaQuirurgicaData,
              showAlert: false,
            });
          }
        ),
        showAlert: true,
      });
    }
  };

  const onStateChange =
    (key, format = null) =>
    (value) => {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        [key]: format ? format(value) : value,
      });
    };

  const isAnyFileUploading = Object.values(uploadingStatus).some((status) => status === true);

  const handleSend = () => {
    if (isAnyFileUploading) {
      return;
    }

    const {
      fechaEgreso,
      motivoEgreso,
      diagnostico,
      zona,
      medicoTratantePrincipal,
      correoElectronico,
      documentos,
    } = formHojaQuirurgicaData;

    if (
      !fechaEgreso ||
      !motivoEgreso ||
      !diagnostico ||
      !zona ||
      !medicoTratantePrincipal ||
      !correoElectronico
    ) {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        showRequired: true,
        alertConfig: buildAlertConfig(
          "error",
          "Campos Requeridos",
          "Debe completar todos los campos requeridos.",
          () => {
            props.formHojaQuirurgica({
              ...formHojaQuirurgicaData,
              showAlert: false,
            });
          }
        ),
        showAlert: true,
      });
      return;
    }

    if (!isValidEmail(correoElectronico)) {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        alertConfig: buildAlertConfig(
          "error",
          "Correo electrónico inválido",
          "Debe ingresar un correo electrónico válido.",
          () => {
            props.formHojaQuirurgica({
              ...formHojaQuirurgicaData,
              showAlert: false,
            });
          }
        ),
        showAlert: true,
      });
      return;
    }

    if (!documentos?.hojaQuirurgica || !documentos?.hojaAnestesia) {
      props.formHojaQuirurgica({
        ...formHojaQuirurgicaData,
        alertConfig: buildAlertConfig(
          "error",
          "Documentos Requeridos",
          "Debe adjuntar obligatoriamente la Hoja Quirúrgica y la Hoja de Anestesia.",
          () => {
            props.formHojaQuirurgica({
              ...formHojaQuirurgicaData,
              showAlert: false,
            });
          }
        ),
        showAlert: true,
      });
      return;
    }

    props.formHojaQuirurgica({
      ...formHojaQuirurgicaData,
      alertConfig: buildAlertConfig(
        "confirm",
        "Confirmación",
        "¿Está seguro de que desea proceder a enviar la documentación para fines de validación?",
        () => {
          props.formHojaQuirurgica({
            ...formHojaQuirurgicaData,
            showAlert: false,
          });
          props.crearSolicitudHojaQuirurgica({
            formHojaQuirurgicaData,
            formHandler: props.formHojaQuirurgica,
            limpiarForm: props.limpiarFormHojaQuirurgica,
            callBack: () => history.push("/pacientes-ingresados"),
          });
        },
        () => {
          props.formHojaQuirurgica({
            ...formHojaQuirurgicaData,
            showAlert: false,
          });
        }
      ),
      showAlert: true,
    });
  };

  const handleCancel = () => {
    props.limpiarFormHojaQuirurgica();
    history.goBack();
  };

  return (
    <CustomLayout showMenuNav={true} footerPosition="relative">
      <Section flexDirection="column" align="center" padding="20px">
        <div style={{ maxWidth: "1000px", width: "100%" }}>
          <TittleLine
            title={"Validación de Hoja Quirúrgica"}
            weight={"bold"}
            maxWidthLine="100%"
          />
          <CustomSeparator height="10px" />
          <p
            style={{
              color: Colors.huGray,
              fontSize: "14px",
              textAlign: "left",
            }}
          >
            Complete la información requerida para el cierre de cuentas
          </p>

          <CustomSeparator height="30px" />

          {/* Información del Afiliado */}
          <TittleLine
            title={"Información del Afiliado"}
            size="18px"
            maxWidthLine="100%"
          />
          <CustomSeparator height="15px" />
          <FormWrapper
            borderColor={empresa?.COLOR_PRINCIPAL}
            maxWidth="100%"
            padding="30px"
          >
            <Form justifyContent="space-between">
              <FormGroup width="30%">
                <CustomInput
                  label={"Nombre del afiliado"}
                  value={formHojaQuirurgicaData.nombreAfiliado}
                  disabled={true}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Número de carnet"}
                  value={formHojaQuirurgicaData.numeroCarnet}
                  disabled={true}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomSelect
                  options={
                    props.camposHojaQuirurgicaData?.planes?.some(
                      (o) => o.value === PLAN_PDSS_JIRA,
                    )
                      ? props.camposHojaQuirurgicaData.planes
                      : [
                          ...(props.camposHojaQuirurgicaData?.planes || []),
                          { value: PLAN_PDSS_JIRA, label: "PDSS" },
                        ]
                  }
                  label={"Plan"}
                  value={formHojaQuirurgicaData.plan || PLAN_PDSS_JIRA}
                  onChange={onStateChange("plan")}
                  width="100%"
                  disabled={true}
                  dataLoading={props.obtenerCamposHojaQuirurgicaLoading}
                  showDefaultOption={false}
                  placeholder={"Seleccionar plan"}
                />
              </FormGroup>
            </Form>
          </FormWrapper>

          <CustomSeparator height="30px" />

          {/* Información del Ingreso */}
          <TittleLine
            title={`Información del Ingreso: ${formHojaQuirurgicaData.numeroIngreso || ""}`}
            size="18px"
            maxWidthLine="100%"
          />
          <CustomSeparator height="15px" />
          <FormWrapper
            borderColor={empresa?.COLOR_PRINCIPAL}
            maxWidth="100%"
            padding="30px"
          >
            <Form justifyContent="space-between">
              <FormGroup width="30%">
                <CustomInput
                  type="date"
                  label={"Fecha de ingreso"}
                  value={formHojaQuirurgicaData.fechaIngreso}
                  disabled={true}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Tipo de servicio"}
                  value={formHojaQuirurgicaData.tipoServicio}
                  disabled={true}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Nombre del PSS"}
                  value={formHojaQuirurgicaData.nombrePss}
                  disabled={true}
                  width="100%"
                />
              </FormGroup>

              <FormGroup width="30%">
                <CustomSelect
                  options={
                    props.camposHojaQuirurgicaData?.tiposPss?.some(
                      (o) => o.value === TIPO_PSS_NO_MEDICO_JIRA,
                    )
                      ? props.camposHojaQuirurgicaData.tiposPss
                      : [
                          ...(props.camposHojaQuirurgicaData?.tiposPss || []),
                          {
                            value: TIPO_PSS_NO_MEDICO_JIRA,
                            label: "No Médico",
                          },
                        ]
                  }
                  label={"Tipo de PSS"}
                  value={
                    formHojaQuirurgicaData.tipoPss || TIPO_PSS_NO_MEDICO_JIRA
                  }
                  onChange={onStateChange("tipoPss")}
                  width="300px"
                  disabled={true}
                  dataLoading={props.obtenerCamposHojaQuirurgicaLoading}
                  showDefaultOption={false}
                  placeholder={"Seleccionar tipo"}
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Código del PSS"}
                  value={
                    formHojaQuirurgicaData.codigoPss ||
                    authUserData?.prestador?.codigo ||
                    ""
                  }
                  disabled={true}
                  width="100%"
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  type="date"
                  label={"Fecha de egreso"}
                  value={formHojaQuirurgicaData.fechaEgreso}
                  onChange={onStateChange("fechaEgreso")}
                  width="100%"
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomSelect
                  label={"Motivo de egreso"}
                  placeholder={"Seleccionar motivo"}
                  options={dataMotivoEgreso}
                  value={formHojaQuirurgicaData.motivoEgreso}
                  onChange={onStateChange("motivoEgreso")}
                  width="300px"
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                  showDefaultOption={true}
                />
              </FormGroup>

              <FormGroup width="30%">
                <CustomInput
                  label={"Diagnóstico"}
                  placeholder={"Diagnóstico"}
                  value={formHojaQuirurgicaData.diagnostico}
                  onChange={onStateChange("diagnostico")}
                  width="100%"
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomSelect
                  options={props.camposHojaQuirurgicaData?.zonasPss || []}
                  label={"Zona"}
                  value={formHojaQuirurgicaData.zona}
                  onChange={onStateChange("zona")}
                  width="300px"
                  dataLoading={props.obtenerCamposHojaQuirurgicaLoading}
                  showDefaultOption={true}
                  placeholder={"Seleccionar zona"}
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Médico tratante principal"}
                  placeholder={"Nombre o código del médico"}
                  value={formHojaQuirurgicaData.medicoTratantePrincipal}
                  onChange={handleMedicoTratantePrincipalChange}
                  suggestions={props?.getMedicosData?.medicos || []}
                  dataLoading={props.getMedicosLoading}
                  updatedSuggestions={props?.getMedicosData?.medicos}
                  width="100%"
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                  disabled={true}
                />
              </FormGroup>
              <FormGroup width="30%">
                <CustomInput
                  label={"Correo electrónico"}
                  placeholder={"correo@ejemplo.com"}
                  value={formHojaQuirurgicaData.correoElectronico || ""}
                  onChange={onStateChange("correoElectronico")}
                  width="100%"
                  required={true}
                  showRequired={formHojaQuirurgicaData.showRequired}
                />
              </FormGroup>

              <FormGroup/>
              
              <FormGroup width="100%">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: "8px",
                    }}
                  >
                    <CustomButton
                      label="Agregar médico (+)"
                      onClick={handleAddMedico}
                      bgColor={empresa?.COLOR_SECUNDARIO}
                      width="180px"
                      height="35px"
                      fontSize="14px"
                      weight="bold"
                    />
                  </div>
                  <CustomInput
                    label={"Médicos adicionales"}
                    placeholder={"Nombre o código del médico"}
                    value={medicoAdicionalInput}
                    onChange={handleMedicoAdicionalInputChange}
                    suggestions={props?.getMedicosData?.medicos || []}
                    dataLoading={props.getMedicosLoading}
                    updatedSuggestions={props?.getMedicosData?.medicos}
                    width="100%"
                  />
                  {formHojaQuirurgicaData.medicosAdicionales?.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "15px",
                      }}
                    >
                      {formHojaQuirurgicaData.medicosAdicionales.map(
                        (medico, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              backgroundColor: "#f0f2f5",
                              border: "1px solid #d9d9d9",
                              borderRadius: "15px",
                              padding: "4px 12px",
                              fontSize: "14px",
                            }}
                          >
                            <span>{medico}</span>
                            <span
                              onClick={() => handleRemoveMedico(index)}
                              style={{
                                cursor: "pointer",
                                color: Colors.huDanger || "#f5222d",
                                fontWeight: "bold",
                                fontSize: "12px",
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              ✕
                            </span>
                          </div>
                        ),
                      )}
                    </div>
                  )}
                </div>
              </FormGroup>
              <FormGroup width="100%">
                <Label font={empresa?.LETRA_PRINCIPAL}>Comentarios</Label>
                <TextArea
                  placeholder="Ingrese sus comentarios aquí..."
                  value={formHojaQuirurgicaData.comentarios || ""}
                  onChange={(e) => onStateChange("comentarios")(e.target.value)}
                  width="100%"
                />
              </FormGroup>
            </Form>
          </FormWrapper>

          <CustomSeparator height="30px" />

          {/* Documentos */}
          <TittleLine
            title={"Detalle del ingreso / Documentos"}
            size="18px"
            maxWidthLine="100%"
          />
          <CustomSeparator height="15px" />
          <FormWrapper
            borderColor={Colors.transparent}
            maxWidth="100%"
            padding="10px 0"
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "15px",
                width: "100%",
              }}
            >
              {[
                {
                  key: "hojaQuirurgica",
                  label: "Hoja Quirúrgica",
                  required: true,
                },
                {
                  key: "hojaAnestesia",
                  label: "Hoja de Anestesia",
                  required: true,
                },
                {
                  key: "estudiosRealizados",
                  label: "Estudios realizados (si aplica)",
                  required: false,
                },
                {
                  key: "historiaClinica",
                  label: "Historia clínica (si aplica)",
                  required: false,
                },
                { key: "otros", label: "Otros (si aplica)", required: false },
              ].map((doc) => {
                const docInfo = formHojaQuirurgicaData.documentos?.[doc.key];
                const isUploading = uploadingStatus[doc.key];

                return (
                  <DocumentUploadRow
                    key={doc.key}
                    docKey={doc.key}
                    label={doc.label}
                    required={doc.required}
                    docInfo={docInfo}
                    isUploading={isUploading}
                    COLOR_PRINCIPAL={empresa?.COLOR_PRINCIPAL}
                    COLOR_SECUNDARIO={empresa?.COLOR_SECUNDARIO}
                    LETRA_PRINCIPAL={empresa?.LETRA_PRINCIPAL}
                    onUpload={handleFileUpload(doc.key)}
                    onRemove={handleRemoveFile(doc.key, docInfo?.tempId)}
                    onView={docInfo ? handleViewFile(docInfo) : null}
                  />
                );
              })}
            </div>
          </FormWrapper>

          <CustomSeparator height="50px" />

          {/* Acciones */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "20px",
              width: "100%",
            }}
          >
            <CustomButton
              label="Enviar solicitud"
              onClick={handleSend}
              bgColor={empresa?.COLOR_SECUNDARIO}
              width="240px"
              height="50px"
              weight="bold"
              fontSize="16px"
              disabled={isAnyFileUploading}
            />
            <CustomButton
              label="Cancelar"
              onClick={handleCancel}
              bgColor={Colors.white}
              textColor={Colors.huMyHealth}
              border={`1px solid ${Colors.huMyHealth}`}
              width="240px"
              height="50px"
              weight="bold"
              fontSize="16px"
            />
          </div>
          <CustomSeparator height="40px" />
        </div>
      </Section>

      <CheckRender allowed={formHojaQuirurgicaData.showAlert}>
        <CustomAlert {...formHojaQuirurgicaData.alertConfig} />
      </CheckRender>

      <CheckRender allowed={props.crearSolicitudHojaQuirurgicaLoading}>
        <ModalLoader />
      </CheckRender>
    </CustomLayout>
  );
};

const mapStateToProps = (state) => ({
  formHojaQuirurgicaData: state.ingreso.formHojaQuirurgicaData,
  camposHojaQuirurgicaData: state.ingreso.camposHojaQuirurgicaData,
  obtenerCamposHojaQuirurgicaLoading:
    state.ingreso.obtenerCamposHojaQuirurgicaLoading,
  authUserData: state.auth.authUserData,
  crearSolicitudHojaQuirurgicaLoading:
    state.ingreso.crearSolicitudHojaQuirurgicaLoading,
  getMedicosData: state.prestador.getMedicosData,
  getMedicosLoading: state.prestador.getMedicosLoading,
});

const mapDispatchToProps = {
  formHojaQuirurgica: IngresoActions.formHojaQuirurgica,
  obtenerCamposHojaQuirurgica: IngresoActions.obtenerCamposHojaQuirurgica,
  crearSolicitudHojaQuirurgica: IngresoActions.crearSolicitudHojaQuirurgica,
  getMedicos: PrestadorActions.getMedicos,
  limpiarFormHojaQuirurgica: IngresoActions.limpiarFormHojaQuirurgica,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(HojaQuirurgicaScreen);
